<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-contenido">
      <h4 class="modal-titulo">Versionar Modelo</h4>

      <p class="mensaje">
        Se creará una nueva versión en blanco del modelo, sin indicadores.
        Podrás agregarlos desde cero. Elige el enfoque de la nueva versión.
      </p>

      <div class="form-grupo">
        <label class="form-label" for="enfoque-version">Enfoque de la versión</label>
        <select id="enfoque-version" v-model="enfoque" class="form-input">
          <option v-for="e in ENFOQUES" :key="e.value" :value="e.value">{{ e.label }}</option>
        </select>
        <span class="enfoque-ayuda">
          Se mostrarán solo los aspectos de este enfoque (más los “General”). Los aspectos de otros enfoques quedan ocultos en la versión.
        </span>
      </div>

      <div class="form-grupo">
        <label class="cambiar-nombre-label">
          <input type="checkbox" v-model="cambiarNombre" class="cambiar-nombre-check" />
          ¿Desea cambiar el nombre del modelo?
        </label>
        <input
          v-if="cambiarNombre"
          v-model="nuevoNombre"
          type="text"
          class="form-input"
          placeholder="Nuevo nombre del modelo"
        />
      </div>

      <div class="botones">
        <button class="btn salir" @click.stop="$emit('cancelar')">Cancelar</button>
        <button class="btn guardar" @click.stop="confirmar">Confirmar</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Creación de una versión nueva a partir de otra.
 *
 * Copia los INDICADORES; la estructura no se re-clona porque es del modelo y se
 * comparte.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, watch } from 'vue'
import { ENFOQUES, type Enfoque } from '@/utils/enfoque'

const props = defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'confirmar', payload: { nuevoNombre?: string; enfoque?: string }): void
  (e: 'cancelar'): void
}>()

const cambiarNombre = ref(false)
const nuevoNombre = ref('')
const enfoque = ref<Enfoque>('general')

watch(() => props.visible, (v) => {
  if (v) {
    cambiarNombre.value = false
    nuevoNombre.value = ''
    enfoque.value = 'general'
  }
})

function confirmar() {
  const nombreFinal = cambiarNombre.value && nuevoNombre.value.trim() ? nuevoNombre.value.trim() : undefined
  emit('confirmar', { nuevoNombre: nombreFinal, enfoque: enfoque.value })
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.48);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.modal-contenido {
  background: white;
  padding: 1.75rem;
  border-radius: 12px;
  width: 480px;
  max-width: 96vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  font-family: var(--font-family);
}

.modal-titulo {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
}

.mensaje {
  font-size: 0.95rem;
  color: #333;
  line-height: 1.5;
  margin-bottom: 1.25rem;
}

.form-grupo {
  margin-bottom: 1.25rem;
}

.form-label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.4rem;
}

.cambiar-nombre-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  margin-bottom: 0.5rem;
}

.cambiar-nombre-check {
  width: auto;
  height: auto;
  accent-color: #0e69a0;
  margin: 0;
  cursor: pointer;
}

.form-input {
  width: 100%;
  padding: 0.55rem 0.85rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: var(--font-family);
  background-color: #f9f9f9;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-input:focus {
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.enfoque-ayuda {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.78rem;
  color: #666;
  line-height: 1.4;
}

.botones {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  padding: 0.6rem 1.3rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: var(--font-family);
  font-size: 0.9rem;
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
  color: #333;
}

.btn.salir:hover {
  background-color: #d41a1a;
  color: white;
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover {
  background-color: #0e69a0;
  color: white;
}
</style>
